import Link from 'next/link'

import clsx from 'clsx'

import type { ComponentProps, JSX } from 'react'

const link =
  'text-ink-secondary no-underline hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

type BreadcrumbProps = Omit<ComponentProps<'nav'>, 'children'> & {
  /** Callers pass every crumb, Home included. The last one is the current page and has no `href`. */
  items: { label: string; href?: string }[]
}

/**
 * Visual trail only; structured data stays in `BreadcrumbListSchema`. The look is the blog's live
 * breadcrumb (S20-1417), which this replaced. Items wrap and the current page may shrink, since blog
 * post titles run to ~100 characters.
 */
export default function Breadcrumb({ items, className, ...rest }: BreadcrumbProps): JSX.Element {
  return (
    <nav aria-label="Breadcrumb" {...rest} className={clsx('font-sans text-base', className)}>
      <ol className="m-0 flex list-none flex-wrap items-center gap-x-2 gap-y-1 p-0">
        {items.map((item, i) => (
          <li key={i} className="flex min-w-0 items-baseline gap-2">
            {i > 0 && (
              <span aria-hidden="true" className="text-ink-muted">
                ›
              </span>
            )}
            {i < items.length - 1 && item.href ? (
              <Link href={item.href} prefetch={false} className={link}>
                {item.label}
              </Link>
            ) : (
              <span aria-current={i === items.length - 1 ? 'page' : undefined} className="text-ink-muted">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}
